import * as React from 'react';
import { ITranslationContext } from "../../../translations";
import * as PropTypes from "prop-types";
import { TimeFormat } from "../../../store/pages/venues/types";
import { ActivityFormatProduct } from "../diary/helpers";
import { clickHandler, isNullOrEmpty } from "../../../utils/util";
import { hasStockConfiguration } from "../../../store/pages/products/helpers";
import { ProductVariant } from "../../../store/pages/products/types";
import { ProductStockLevel, ProductStockStatus } from "../../../store/pages/productStock/types";

interface ProductsSelectorProps {
    activityFormatProduct: ActivityFormatProduct;
    timeFormat: TimeFormat;
    onSelectProduct: (product: ActivityFormatProduct, quantity: number, productVariantId: string | null) => void;
    productStockLevels: ProductStockLevel[];
}

type CommonStockAttributes = {
    hasStockData: boolean;
    currentStock: number | null;
    stockState: ProductStockStatus;
    style: React.CSSProperties;
};

type VariantAttributes = CommonStockAttributes & {
    variantId: string
};

type ProductAttributes = CommonStockAttributes & {
    hasVariants: boolean;
    variantAttributes: VariantAttributes[];
};

type Coords = { x: number; y: number };

const noStock = { availableStock: 0, stockState: ProductStockStatus.Ok, hasStockData: false }
interface StockLevel {
    hasStockData: boolean;
    availableStock: number;
    stockState: ProductStockStatus;
}

const ProductListItem = (props: ProductsSelectorProps, context: ITranslationContext) => {
    const { activityFormatProduct, timeFormat, onSelectProduct, productStockLevels } = props;
    const { product } = activityFormatProduct;
    const { t } = context;

    const isStockConfiguredForProduct = React.useMemo<boolean>(() => hasStockConfiguration(product), [product]);

    const [showVariantSelectionPopup, setShowVariantSelectionPopup] = React.useState<boolean>(false);
    const [variantSelectionPopupCoords, setVariantSelectionPopupCoords] = React.useState<Coords | null>(null);

    const buildItemStyle = (imageUrl: string | null | undefined, stockState: ProductStockStatus): React.CSSProperties => {
        const imageStyle: React.CSSProperties = !isNullOrEmpty(imageUrl) && imageUrl
            ? {
                backgroundImage: `url(${imageUrl})`,
                backgroundPosition: 'center',
                backgroundSize: 'contain',
                backgroundRepeat: 'no-repeat'
            }
            : {};

        const borderStyle: React.CSSProperties =
            stockState === ProductStockStatus.OutOfStock ? { border: '4px solid #FF0000' }
                : stockState === ProductStockStatus.LowStock ? { border: '4px solid #FFCC00' }
                    : {}

        return {
            ...imageStyle,
            ...borderStyle
        };
    }

    const getStockLevel = (productId: string, variantId: string | null): StockLevel => {
        if (!isStockConfiguredForProduct) return noStock;

        const { productStockLevels } = props;

        const stockLevel = productStockLevels.find(sl => sl.productId === productId && sl.productVariantId === variantId);
        if (!stockLevel) return noStock;

        const quantityInfo = stockLevel.quantities.length > 0 ? stockLevel.quantities[0] : null;
        if (!quantityInfo) return noStock;

        return {
            hasStockData: true,
            availableStock: quantityInfo.availableStock,
            stockState: quantityInfo.stockState
        };
    }

    const productAttributes = React.useMemo<ProductAttributes>(() => {
        const hasVariants = isStockConfiguredForProduct && product.variants.length > 0;
        const productStockLevel = getStockLevel(product.id, null);

        const variantAttributes: VariantAttributes[] = hasVariants
            ? product.variants.map((variant) => {
                const variantStockLevel = getStockLevel(product.id, variant.id);
                const variantStockState = variantStockLevel.stockState
                return {
                    hasStockData: variantStockLevel.hasStockData,
                    variantId: variant.id,
                    currentStock: variantStockLevel.availableStock,
                    stockState: variantStockState,
                    style: buildItemStyle(variant.productVariantImageUrl || product.productImageUrl, variantStockState),
                }
            })
            : [];

        const stockState = productStockLevel.stockState
        return {
            hasStockData: productStockLevel.hasStockData,
            currentStock: productStockLevel.availableStock,
            stockState: stockState,
            style: buildItemStyle(product.productImageUrl, stockState),
            hasVariants,
            variantAttributes
        };
    }, [product, productStockLevels]);

    const handleProductSelectionClick = (e: React.MouseEvent<HTMLLIElement>, activityFormatProduct: ActivityFormatProduct) => {
        if (isStockConfiguredForProduct && product.variants.length > 0) {
            const rect = e.currentTarget.getBoundingClientRect();
            const x = e.clientX;
            const y = e.clientY;
            setVariantSelectionPopupCoords({ x, y });
            setShowVariantSelectionPopup(true);
        } else {
            onSelectProduct(activityFormatProduct, 1, null);
        }
    }

    const closeVariantSelectionPopup = () => {
        setShowVariantSelectionPopup(false)
        setVariantSelectionPopupCoords(null);
    };

    const handleProductVariantSelectionClick = (variant: ProductVariant) => {
        onSelectProduct(activityFormatProduct, 1, variant.id);
        closeVariantSelectionPopup();
    }

    const renderStockInformation = (attributes: CommonStockAttributes | undefined) => {
        const hasStockData = attributes?.hasStockData ?? false;
        if (!hasStockData) {
            return null;
        }

        return <div className='pos_product_item_stockinfo'>{attributes?.currentStock} {t('ProductSelector:available')}</div>
    }

    return (
        <>
            <li
                className='pos_product_item'
                onClick={e => clickHandler(e, () => handleProductSelectionClick(e, activityFormatProduct))}
                style={productAttributes.style}>
                <div className='pos_product_item_text'>{activityFormatProduct.reservationTime ? activityFormatProduct.reservationTime.toShortTimeString(timeFormat) : null} {activityFormatProduct.displayName}</div>
                {renderStockInformation(productAttributes)}
                {productAttributes.hasVariants && (
                    <div className='pos_product_item_icon'>
                        <i className='glyphicon glyphicon-option-horizontal' />
                    </div>
                )}
            </li>

            {productAttributes.hasVariants && showVariantSelectionPopup && (
                <>
                    <div className="pos_product_variant_overlay" />
                    <div
                        className="pos_product_variant_popup"
                        style={{ position: 'fixed', top: variantSelectionPopupCoords?.y ?? 0, left: variantSelectionPopupCoords?.x ?? 0, transform: 'translate(-50%, 0)', zIndex: 100 }}
                    >
                        <ul className='list-unstyled pos_product_list'>
                            {product.variants.map((variant) => {
                                const variantAttributes = productAttributes.variantAttributes.find(va => va.variantId === variant.id);
                                return (
                                    <li
                                        key={variant.id}
                                        className='pos_product_item'
                                        onClick={e => clickHandler(e, () => handleProductVariantSelectionClick(variant))}
                                        style={variantAttributes!.style}>
                                        <div className='pos_product_item_text'>{variant.name}</div>
                                        {renderStockInformation(variantAttributes)}
                                    </li>
                                )
                            })}
                            <li key='close' className='pos_product_item' onClick={e => clickHandler(e, closeVariantSelectionPopup)} >
                                <button style={{ height: '100%', fontSize: '30px', fontWeight: 'bold' }} className='btn btn-block btn-info ' onClick={e => clickHandler(e, closeVariantSelectionPopup)}>
                                    {t('Global:cancel')}
                                </button>
                            </li>
                        </ul>
                    </div>
                </>
            )}
        </>
    )
}

ProductListItem.contextTypes = {
    t: PropTypes.func
}

export default ProductListItem;
